<template>
  <teleport to="body">
    <div v-if="modelValue" class="global-search-backdrop" @click.self="close">
      <div class="global-search-backdrop-layer has-background-dark"></div>

      <div class="global-search-modal box">
        <div class="global-search-header">
          <div class="global-search-header-left">
            <div class="global-search-title-icon">
              <font-awesome-icon icon="magnifying-glass" />
            </div>
            <div>
              <div class="global-search-title">Ricerca globale</div>
              <div class="global-search-subtitle">Clienti, luoghi, impianti, appuntamenti, chiamate</div>
            </div>
          </div>

          <button class="global-search-close button is-small is-light" @click="close" aria-label="Chiudi ricerca">
            <font-awesome-icon icon="times" />
          </button>
        </div>

        <div class="field">
          <label class="label">Cerca</label>
          <div class="control has-icons-left">
            <span class="icon is-small is-left">
              <font-awesome-icon icon="magnifying-glass" />
            </span>
            <input
              ref="searchInput"
              class="input"
              type="text"
              placeholder="Clienti, luoghi, impianti, appuntamenti, chiamate"
              v-model="query"
              @input="onInput"
            >
          </div>
        </div>

        <div class="global-search-results" :class="{'is-loading': loading}">
          <div v-if="loading" class="has-text-grey">Ricerca in corso...</div>

          <template v-else>
            <div v-if="query.trim().length < minLength" class="has-text-grey">
              Inserisci almeno {{ minLength }} caratteri
            </div>

            <div v-else-if="!flatResults.length" class="has-text-grey">
              Nessun risultato
            </div>

            <button
              v-for="result in flatResults"
              :key="result.key"
              class="global-search-item box"
              @click="openResult(result)"
              type="button"
            >
              <div class="implant-search">
                <div class="implant-search-row">
                  <div class="implant-search-col">
                    <div class="implant-search-label">Tipo</div>
                    <div class="implant-search-value">
                      <span class="tag is-light">{{ result.entityLabel }}</span>
                    </div>
                  </div>
                  <div class="implant-search-col">
                    <div class="implant-search-label">Titolo</div>
                    <div class="implant-search-value">{{ result.title }}</div>
                  </div>
                </div>

                <div class="implant-search-row" v-if="result.subtitle">
                  <div class="implant-search-label">Dettaglio</div>
                  <div class="implant-search-value">{{ result.subtitle }}</div>
                </div>

                <div class="implant-search-row" v-if="result.tags?.length">
                  <div class="implant-search-label">Info</div>
                  <div class="implant-search-value">
                    <div class="tags are-small">
                      <span class="tag is-light" v-for="(tag, index) in result.tags" :key="index">{{ tag }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </button>
          </template>
        </div>
      </div>
    </div>
  </teleport>
</template>

<script>
export default {
  props: {
    modelValue: {
      type: Boolean,
      default: false
    },
    minLength: {
      type: Number,
      default: 2
    },
    debounceMs: {
      type: Number,
      default: 500
    },
    limit: {
      type: Number,
      default: 8
    }
  },
  emits: ['update:modelValue'],
  data() {
    return {
      query: '',
      loading: false,
      rawResults: {
        clients: [],
        places: [],
        implants: [],
        operations: [],
        calls: []
      },
      debouncedSearch: null
    }
  },
  computed: {
    flatResults() {
      const rows = []

      this.rawResults.clients.forEach(client => {
        rows.push({
          key: 'client-' + client._id,
          entityLabel: 'Cliente',
          title: this.$clientName(client) || 'Cliente',
          subtitle: this.$fulladdress(client),
          tags: [client.city, client.zip].filter(Boolean),
          route: '/clienti/' + client._id
        })
      })

      this.rawResults.places.forEach(place => {
        rows.push({
          key: 'place-' + place._id,
          entityLabel: 'Luogo',
          title: this.$placeName(place) || 'Luogo',
          subtitle: this.$clientName(place.client),
          tags: [place.floor ? 'P. ' + place.floor : null, place.interior ? 'Int. ' + place.interior : null].filter(Boolean),
          route: '/luoghi/' + place._id
        })
      })

      this.rawResults.implants.forEach(implant => {
        const implantLines = (this.$implantName(implant) || '').split('\n').map(line => line.trim()).filter(Boolean)
        rows.push({
          key: 'implant-' + implant._id,
          entityLabel: 'Impianto',
          title: implantLines[0] || 'Impianto',
          subtitle: this.$placeName(implant.place_occupant),
          tags: [this.$clientName(implant.occupant), this.$clientName(implant.client), implant.archived ? 'Archiviato' : null].filter(Boolean),
          route: '/impianti/' + implant._id
        })
      })

      this.rawResults.operations.forEach(operation => {
        const date = operation.appointment_date ? new Date(operation.appointment_date).toDisplay() : null
        rows.push({
          key: 'operation-' + operation._id,
          entityLabel: 'Appuntamento',
          title: operation.type || 'Appuntamento',
          subtitle: [this.$clientName(operation.occupant || operation.client), this.$placeName(operation.place)].filter(Boolean).join(' • '),
          tags: [operation.progressive ? 'N. ' + operation.progressive : null, operation.status, date].filter(Boolean),
          route: '/appuntamenti/' + operation._id
        })
      })

      this.rawResults.calls.forEach(call => {
        const date = call.date ? new Date(call.date).toDisplay() : null
        const fallbackClient = this.$clientName(call.client || call.occupant) || this.$clientName(call.tempClient)
        const fallbackPlace = this.$placeName(call.place) || this.$placeName(call.tempClient)
        rows.push({
          key: 'call-' + call._id,
          entityLabel: 'Chiamata',
          title: call.type || 'Chiamata',
          subtitle: [fallbackClient, fallbackPlace].filter(Boolean).join(' • '),
          tags: [call.priority, date].filter(Boolean),
          route: '/chiamate/' + call._id
        })
      })

      return rows
    }
  },
  methods: {
    close() {
      this.$emit('update:modelValue', false)
    },
    clearResults() {
      this.rawResults = {
        clients: [],
        places: [],
        implants: [],
        operations: [],
        calls: []
      }
    },
    onInput() {
      if (!this.debouncedSearch) {
        return
      }
      this.debouncedSearch(this.query)
    },
    fetchResults(text) {
      const cleanText = (text || '').trim()
      if (cleanText.length < this.minLength) {
        this.loading = false
        this.clearResults()
        return
      }

      this.loading = true

      this.$http.get('/search', { params: { q: cleanText, limit: this.limit } })
        .then(response => {
          this.rawResults = {
            clients: response?.data?.clients || [],
            places: response?.data?.places || [],
            implants: response?.data?.implants || [],
            operations: response?.data?.operations || [],
            calls: response?.data?.calls || []
          }
          this.loading = false
        })
        .catch(error => {
          console.log(error)
          this.loading = false
          this.clearResults()
        })
    },
    openResult(result) {
      this.$router.push(result.route)
      this.close()
    },
    onEsc(event) {
      if (event.key === 'Escape' && this.modelValue) {
        this.close()
      }
    }
  },
  mounted() {
    this.debouncedSearch = this.$debounce(this.fetchResults.bind(this), this.debounceMs)
    window.addEventListener('keydown', this.onEsc)
  },
  beforeUnmount() {
    window.removeEventListener('keydown', this.onEsc)
  },
  watch: {
    modelValue(value) {
      if (value) {
        this.$nextTick(() => {
          this.$refs.searchInput?.focus()
        })
      } else {
        this.query = ''
        this.loading = false
        this.clearResults()
      }
    }
  }
}
</script>

<style scoped>
.global-search-backdrop {
  position: fixed;
  inset: 0;
  backdrop-filter: blur(6px);
  -webkit-backdrop-filter: blur(6px);
  z-index: 999;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 80px 16px 16px;
}

.global-search-backdrop-layer {
  position: absolute;
  inset: 0;
  opacity: 0.35;
}

.global-search-modal {
  position: relative;
  z-index: 1;
  width: min(900px, 100%);
  max-height: calc(100vh - 96px);
  overflow: hidden;
  border-radius: 12px;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.global-search-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding-bottom: 10px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
}

.global-search-header-left {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
}

.global-search-title-icon {
  width: 34px;
  height: 34px;
  border-radius: 999px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: rgba(72, 95, 199, 0.1);
  color: #485fc7;
  flex: 0 0 auto;
}

.global-search-title {
  display: inline-flex;
  align-items: center;
  font-weight: 700;
  font-size: 1.05rem;
  line-height: 1.1;
}

.global-search-subtitle {
  font-size: 0.8rem;
  color: rgba(54, 54, 54, 0.75);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.global-search-close {
  border-radius: 999px;
  width: 34px;
  height: 34px;
  padding: 0;
  min-width: 34px;
}

.global-search-close :deep(svg) {
  margin-right: 0;
}

.global-search-results {
  overflow: auto;
  min-height: 120px;
}

.global-search-item {
  display: block;
  width: 100%;
  text-align: left;
  margin-bottom: 8px;
  padding: 8px;
  cursor: pointer;
}

.global-search-item:hover {
  transform: translateY(-1px);
}

.implant-search {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.implant-search-row {
  display: flex;
  gap: 12px;
  align-items: center;
  flex-wrap: wrap;
}

.implant-search-col {
  min-width: 160px;
}

.implant-search-label {
  font-size: 0.7rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: inherit;
  opacity: 0.7;
}

.implant-search-value {
  font-weight: 600;
}

@media screen and (max-width: 768px) {
  .global-search-backdrop {
    padding: 60px 10px 10px;
  }

  .global-search-modal {
    max-height: calc(100vh - 72px);
    border-radius: 10px;
    padding: 12px;
  }

  .global-search-header {
    align-items: flex-start;
  }

  .global-search-subtitle {
    white-space: normal;
    line-height: 1.25;
    margin-top: 2px;
  }

  .implant-search-col {
    min-width: 120px;
  }
}
</style>
